import type { GetMmProgramsResponse, MmProgramSummary, ScheduleChangeKind } from '@/services/api/mm-programs';
import { utcDaysBetween } from '@/utils/date';
export const UPCOMING_WINDOW_DAYS = 30;

export interface DashboardOverview {
  activeMarketMakers: number;
  totalMarketMakers: number;
  liveInstruments: number;
  /** Monthly USD at full KPI, active market makers only */
  committedMonthlyUsd: number;
  changesThisWeek: number;
  needsAttention: MmProgramSummary[];
}

export interface UpcomingChange {
  date: string;
  daysAway: number;
  kind: ScheduleChangeKind;
  instrument: string;
  marketMaker: Pick<MmProgramSummary, 'p' | 'name' | 'active'>;
}

export const CHANGE_LABELS: Record<ScheduleChangeKind, string> = {
  starts: 'Starts',
  ends: 'Ends',
  changes: 'Switches',
};

export const findUpcomingChanges = (response: Pick<GetMmProgramsResponse, 'asOf' | 'marketMakers'>, windowDays = UPCOMING_WINDOW_DAYS): UpcomingChange[] =>
  response.marketMakers
    .flatMap((marketMaker) =>
      marketMaker.instruments.flatMap((summary) => {
        if (!summary.nextChange) return [];
        const daysAway = utcDaysBetween(response.asOf, summary.nextChange.date);
        if (daysAway > windowDays) return [];
        return [
          {
            date: summary.nextChange.date,
            daysAway,
            kind: summary.nextChange.kind,
            instrument: summary.instrument,
            marketMaker: { p: marketMaker.p, name: marketMaker.name, active: marketMaker.active },
          },
        ];
      }),
    )
    .sort((left, right) => left.date.localeCompare(right.date) || left.marketMaker.name.localeCompare(right.marketMaker.name) || left.instrument.localeCompare(right.instrument));

export const summariseDashboard = (response: Pick<GetMmProgramsResponse, 'asOf' | 'marketMakers'>): DashboardOverview => {
  const active = response.marketMakers.filter((marketMaker) => marketMaker.active);
  return {
    activeMarketMakers: active.length,
    totalMarketMakers: response.marketMakers.length,
    liveInstruments: active.reduce((total, marketMaker) => total + marketMaker.liveInstruments, 0),
    committedMonthlyUsd: active.reduce((total, marketMaker) => total + marketMaker.monthlyCompensationUsd, 0),
    changesThisWeek: findUpcomingChanges(response, 7).length,
    needsAttention: response.marketMakers.filter((marketMaker) => marketMaker.problems.length > 0),
  };
};
